import { stepsAnatomy } from '@chakra-ui/react/anatomy';
import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

const STATUS_COMPLETED = '&[data-step-status=completed]';
const STATUS_DISABLED = '&[data-step-status=disabled]';
const STATUS_IN_PROGRESS = '&[data-step-status=inProgress]';
const STATUS_INVALID = '&[data-step-status=invalid]';
const STATUS_NOT_STARTED = '&[data-step-status=notStarted]';
const STATUS_SKIPPABLE = '&[data-step-status=skippable]';
const STATUS_SKIPPED = '&[data-step-status=skipped]';

const truncate = { maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

const stepsSlotRecipe = defineSlotRecipe({
  className: 'steps',
  slots: stepsAnatomy.keys(),
  base: {
    root: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    list: {
      alignItems: 'stretch',
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
    },
    item: {
      alignItems: 'flex-start',
      borderColor: 'border/disabled',
      borderInlineStartWidth: '2',
      borderStyle: 'solid',
      display: 'flex',
      flex: '1 0 0',
      gap: '8',
      paddingBlockEnd: '24',
      paddingInlineEnd: '24',
      paddingInlineStart: '12',

      '&:last-of-type': {
        paddingBlockEnd: 0,
      },

      [`${STATUS_COMPLETED}, ${STATUS_SKIPPED}`]: { borderColor: 'border/selected' },
      [STATUS_IN_PROGRESS]: {
        position: 'relative',

        _before: {
          background: 'border/selected',
          content: '""',
          height: '50%',
          insetBlockStart: 0,
          insetInlineStart: '-2px',
          position: 'absolute',
          width: '2',
        },
      },
    },
    indicator: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexShrink: '0',
      borderRadius: '9999px',
      borderWidth: '2',
      borderStyle: 'solid',
      width: '24',
      height: '24',
      overflow: 'clip',
      textStyle: 'body/md/semibold',
      lineHeight: 'initial',

      [STATUS_NOT_STARTED]: {
        bg: 'background/primary',
        borderColor: 'border/strong',
        color: 'text/secondary',
      },
      [STATUS_IN_PROGRESS]: { bg: 'interactive/subtle', borderColor: 'border/selected', color: 'text/selected' },
      [STATUS_COMPLETED]: { bg: 'interactive/base', borderColor: 'border/selected', color: 'icon/on-color' },
      [STATUS_INVALID]: {
        bg: 'status/critical/bg',
        borderColor: 'border/error',
        color: 'text/negative',
        textStyle: 'heading/h4',
      },
      [`${STATUS_SKIPPABLE}, ${STATUS_SKIPPED}, ${STATUS_DISABLED}`]: {
        bg: 'background/primary',
        borderColor: 'border/disabled',
        color: 'text/disabled',
      },
    },
    trigger: {
      alignItems: 'flex-start',
      display: 'flex',
      flex: '1 0 0',
      flexDirection: 'column',
      gap: '2',
      minWidth: '1',
      paddingBlockStart: '4',
    },
    title: {
      ...truncate,
      textStyle: 'body/md/regular',
      color: 'text/secondary',
      lineHeight: 'initial!',

      '&[data-selected]': { textStyle: 'body/md/semibold', color: 'text/primary' },
      [`${STATUS_SKIPPABLE}, ${STATUS_SKIPPED}, ${STATUS_DISABLED}`]: { color: 'text/disabled' },
    },
    description: {
      ...truncate,
      textStyle: 'body/sm/regular',
      color: 'text/secondary',

      [`${STATUS_SKIPPABLE}, ${STATUS_SKIPPED}`]: { color: 'text/tertiary' },
      [STATUS_DISABLED]: { color: 'text/disabled' },
    },
    // Required by stepsAnatomy but unused in this component
    content: {},
    nextTrigger: {},
    prevTrigger: {},
    progress: {},
    separator: {},
  },
  variants: {
    orientation: {
      horizontal: {
        list: {
          tablet: {
            alignItems: 'center',
            flexDirection: 'row',
          },
        },
        item: {
          tablet: {
            alignItems: 'center',
            borderBlockStartWidth: '2',
            borderInlineStartWidth: 0,
            paddingBlock: '16',
            paddingInlineStart: 0,

            '&:last-of-type': {
              paddingBlockEnd: '16',
            },

            [STATUS_IN_PROGRESS]: {
              _before: {
                height: '2',
                insetBlockStart: '-2px',
                insetInlineStart: 0,
                width: '50%',
              },
            },
          },
        },
        trigger: {
          tablet: {
            alignItems: 'center',
            flexDirection: 'row',
            gap: '8',
            paddingBlockStart: 0,
          },
        },
        description: {
          tablet: { display: 'none' },
        },
      },
      vertical: {},
    },
  },
  defaultVariants: {
    orientation: 'horizontal',
  },
});

export default stepsSlotRecipe;
